import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation
} from '@angular/core';

import { Observable, of, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { DealFacade } from '../../facades/deal.facade';
import { setupPopupDraggable } from '../../helpers/deals/popup-drag';
import { Globals } from '../../services/globals';
import { BidResultsWithType } from '../../state/bid-results/state';
import { DealInformation } from '../../types/deal.types';
import { ManagerGridViewModel } from '../../types/deal.viewmodel.types';

export interface BidResultsModalData {
  bidResults: BidResultsWithType;
  deal: ManagerGridViewModel | undefined;
  fromBidCompare: boolean;
  closeBidResultsModal: () => void;
}

@Component({
  templateUrl: './bid-results-modal.component.html',
  selector: 'muni-shared-bid-results-modal',
  styleUrls: ['./bid-results-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class BidResultsModalComponent
  implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    private dealFacade: DealFacade,
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private globals: Globals
  ) { }

  private _context: BidResultsModalData;

  public get context(): BidResultsModalData {
    return this._context;
  }

  @Input()
  public set context(value: BidResultsModalData) {
    this._context = value;
    if (value) {
      this.changeDetectorRef.markForCheck();
    }
  }

  @Input()
  public response: Subject<any>;
  public data$: Observable<BidResultsModalData>;
  public bidResults$: Observable<BidResultsWithType>;
  public hasParColumn$: Observable<boolean>;
  public eventListeners: (() => void)[] = [];

  public readonly winningOrionId$: Observable<number> = this.dealFacade.dealState$.pipe(
    filter((dealInfo: DealInformation) => dealInfo.loaded),
    map(
      (dealInfo) =>
        dealInfo.deal.seniorManager && dealInfo.deal.seniorManager.orionId
    )
  );

  ngAfterViewInit(): void {
    this.eventListeners = setupPopupDraggable(
      this.renderer,
      this.elementRef,
      'bid-results-modal-drag',
      true
    );
    setTimeout(() => {
      this.adjustBackdropSize();
    });
  }

  public ngOnInit() {
    this.data$ = of(this.context);
    this.hasParColumn$ = this.data$.pipe(
      map(({ bidResults }) =>
        bidResults.bids.some(
          ({ parAmount }) => !isNaN(parAmount) && parAmount !== null
        )
      )
    );

    this.bidResults$ = this.data$.pipe(map(({ bidResults }) => bidResults));
  }

  public closeModal() {
    this.removeOverlay();
    this.response.complete();
  }

  private removeOverlay() {
    this.globals.nativeWindow.document.body.classList.remove(
      'bid-compare-overlay'
    );
  }

  @HostListener('window:resize')
  onResize() {
    this.adjustBackdropSize();
  }

  adjustBackdropSize() {
    if (!this._context.fromBidCompare) {
      return;
    }

    const container = this.globals.nativeWindow.document.querySelector(
      'muni-bidcomp-shared-compare'
    ) as HTMLElement;
    const backdrop = this.globals.nativeWindow.document.querySelector(
      '.bid-compare-overlay .c-modal-backdrop'
    ) as HTMLElement;
    const modalContainer = this.globals.nativeWindow.document.querySelector(
      '.compare-bid-results-modal'
    ) as HTMLElement;

    if (container && backdrop) {
      const { top, left, width, height } = container.getBoundingClientRect();

      // Position backdrop over the component
      backdrop.style.position = 'fixed';
      backdrop.style.top = `${top}px`;
      backdrop.style.left = `${left}px`;
      backdrop.style.width = `${width}px`;
      backdrop.style.height = `${height}px`;

      // set modal container styles
      const gap = 6;
      const modalWidth = modalContainer.offsetWidth;
      modalContainer.style.top = `${top + 6}px`;
      modalContainer.style.left = `${left + (width - modalWidth) / 2}px`;
      modalContainer.style.maxHeight = `${height - (gap * 2)}px`;
    }
  }

  public ngOnDestroy() {
    // Remove all event listeners
    this.eventListeners.forEach((eventListener) => eventListener());
    this.removeOverlay();
  }
}
